import { jsPDF } from "jspdf";

export default function generateRentalReport(data, selectedDate = null) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  // Use the selected date if provided, otherwise try to extract from data, otherwise use current date
  let formattedDate;
  if (selectedDate) {
    formattedDate = selectedDate;
  } else {
    let latestReportDate = null;
    data.forEach(helicopter => {
      if (helicopter.reportDate) {
        const currentDate = new Date(helicopter.reportDate);
        if (!latestReportDate || currentDate > latestReportDate) {
          latestReportDate = currentDate;
        }
      }
    });
    formattedDate = latestReportDate ? latestReportDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
  }

  // Column definitions for consistent layout (defined early to avoid hoisting issues)
  // Reduced widths to fit more content on page
  const columns = [
    { header: "Last Name", width: 38, key: 'lastName' },
    { header: "First Name", width: 38, key: 'firstName' },
    { header: "Rentals", width: 42, key: 'rentals' },
    { header: "Preferred Ski/Board", width: 68, key: 'preferredSkiBoard' },
    { header: "Size", width: 50, key: 'size' },
  ];

  // Function to wrap text within a column width (defined early to avoid hoisting issues)
  const wrapText = (doc, text, maxWidth) => {
    if (!text) return [''];

    const words = text.toString().split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const textWidth = doc.getTextWidth(testLine);

      if (textWidth <= maxWidth - 4) { // Leave 4 units padding
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Single word is too long, truncate it
          lines.push(word.substring(0, Math.floor(word.length * maxWidth / textWidth)));
        }
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [''];
  };

  // Function to draw text in a cell with proper boundaries (defined early to avoid hoisting issues)
  const drawCellText = (doc, text, x, y, width, height) => {
    const lines = wrapText(doc, text, width);
    const lineHeight = 3;

    // Draw each line of text
    lines.forEach((line, index) => {
      if (index * lineHeight < height - 2) { // Make sure text fits in cell height
        doc.text(line, x + 2, y + 3 + (index * lineHeight));
      }
    });

    return lines.length * lineHeight;
  };

  console.log(data);

  // Function to draw page header (defined early to avoid hoisting issues)
  const drawPageHeader = (doc, formattedDate, totalRentalsDay, totalSkisDay, totalBoardsDay) => {
    const primaryColor = '#c8d701';
    const redColor = '#f04e26';

    doc.setFontSize(16);
    doc.setTextColor(primaryColor);
    doc.text(`Rental Report ${formattedDate}`, 148, 10, { align: "center" });

    // Add summary line at the top
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("Total Rentals Day: ", 10, 16);
    doc.setTextColor(redColor);
    doc.text(`${totalRentalsDay}`, 45, 16);

    doc.setTextColor(0, 0, 0);
    doc.text("Total Skis Day: ", 65, 16);
    doc.setTextColor(redColor);
    doc.text(`${totalSkisDay}`, 95, 16);

    doc.setTextColor(0, 0, 0);
    doc.text("Total Boards Day: ", 115, 16);
    doc.setTextColor(redColor);
    doc.text(`${totalBoardsDay}`, 150, 16);
    doc.setTextColor(0, 0, 0);

    return 22; // Return Y position after header (reduced from 27)
  };

  // Get the totals from the last helicopter in the data array
  const lastHelicopter = data && data.length > 0 ? data[data.length - 1] : null;
  const totalRentalsDay = lastHelicopter?.totalRental || 0;
  const totalSkisDay = lastHelicopter?.totalski || 0;
  const totalBoardsDay = lastHelicopter?.totalboard || 0;

  let yPos = drawPageHeader(doc, formattedDate, totalRentalsDay, totalSkisDay, totalBoardsDay);

  // Function to draw table headers using the column system
  const drawHeaders = (doc, y) => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');

    let x = 10;
    columns.forEach(col => {
      doc.setDrawColor(0);
      doc.setFillColor(0, 0, 0); // Black background for headers
      doc.rect(x, y, col.width, 6, 'FD');
      doc.setTextColor(255, 255, 255); // White text
      doc.text(col.header, x + col.width/2, y + 4, { align: "center" });
      x += col.width;
    });

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0); // Reset to black
    return y + 6;
  };

  // Removed duplicate drawPageHeader function - now defined earlier in the file

  // Function to render a client using the column system
  const renderClient = (doc, client, y) => {
    doc.setFontSize(7);

    // Calculate the row height needed based on text wrapping
    let maxRowHeight = 6; // Minimum row height (reduced from 8)

    // First pass: calculate required height for this row
    columns.forEach(col => {
      let cellText = '';
      if (col.key === 'lastName') {
        cellText = client.lastName || 'N/A';
      } else if (col.key === 'firstName') {
        cellText = client.firstName || 'N/A';
      } else if (col.key === 'rentals') {
        cellText = client.rentals || 'N/A';
      } else if (col.key === 'preferredSkiBoard') {
        cellText = client.preferredSkiBoard || 'N/A';
      } else if (col.key === 'size') {
        cellText = client.size || 'N/A';
      }

      const lines = wrapText(doc, cellText, col.width);
      const requiredHeight = Math.max(6, lines.length * 2.5 + 1.5);
      maxRowHeight = Math.max(maxRowHeight, requiredHeight);
    });

    // No page break check needed - handled at group level

    // Draw cell borders and content
    let x = 10;
    columns.forEach(col => {
      // Draw cell border
      doc.setDrawColor(200, 200, 200); // Light gray border
      doc.setFillColor(255, 255, 255);
      doc.rect(x, y, col.width, maxRowHeight, 'FD');

      // Get cell content
      let cellText = '';
      if (col.key === 'lastName') {
        cellText = client.lastName || 'N/A';
      } else if (col.key === 'firstName') {
        cellText = client.firstName || 'N/A';
      } else if (col.key === 'rentals') {
        cellText = client.rentals || 'N/A';
      } else if (col.key === 'preferredSkiBoard') {
        cellText = client.preferredSkiBoard || 'N/A';
      } else if (col.key === 'size') {
        cellText = client.size || 'N/A';
      }

      // Set text color
      doc.setTextColor(0, 0, 0);

      // Draw cell content
      drawCellText(doc, cellText, x, y, col.width, maxRowHeight);

      x += col.width;
    });

    return y + maxRowHeight;
  };

  // Start processing each helicopter and its groups
  data.forEach((helicopter) => {
    let totalski = 0;
    let totalboard = 0;

    if (helicopter.heliIndex != 1) {
      doc.addPage('landscape');
      yPos = drawPageHeader(doc, formattedDate, totalRentalsDay, totalSkisDay, totalBoardsDay);
    }

    helicopter.groups.forEach(group => {
      // Calculate total height needed for this entire group
      let groupHeaderHeight = 5; // Group header (reduced from 7)
      let tableHeaderHeight = 6; // Table headers (reduced from 8)
      let spacingAfterHeaders = 1; // Reduced from 2
      let clientRowsHeight = 0;
      let spacingAfterGroup = 3; // Reduced from 5

      // Calculate height for all clients (minimum 6 per client)
      group.clients.forEach(() => {
        clientRowsHeight += 6; // Minimum row height per client (reduced from 8)
      });

      const totalGroupHeight = groupHeaderHeight + tableHeaderHeight + spacingAfterHeaders + clientRowsHeight + spacingAfterGroup;

      // Check if entire group fits on current page, if not start new page
      if (yPos + totalGroupHeight > 190) {
        doc.addPage('landscape');
        yPos = drawPageHeader(doc, formattedDate, totalRentalsDay, totalSkisDay, totalBoardsDay);
      }

      // Group header
      doc.setFontSize(9);
      doc.setTextColor('#c8d701'); // Primary color for group header
      doc.text(`Heli #${helicopter.heliIndex} - Group #${group.groupIndex} (H${helicopter.heliIndex}G${group.groupIndex})`, 10, yPos);
      yPos += 5;

      // Draw the headers
      yPos = drawHeaders(doc, yPos);
      yPos += 1;

      // Render all clients (no page break check needed - handled at group level)
      group.clients.forEach(client => {
        yPos = renderClient(doc, client, yPos);
      });

      yPos += 3; // Space between groups (reduced from 5)
      totalski += group.groupski;
      totalboard += group.groupboard;
    });

    // Footer with total counts
    if (yPos + 8 > 190) {
      doc.addPage('landscape');
      yPos = drawPageHeader(doc, formattedDate, totalRentalsDay, totalSkisDay, totalBoardsDay);
    }

    doc.setFontSize(10);
    let totalGuestRentals = helicopter.groups.reduce((acc, group) => acc + group.totalGroupRentals, 0);
    doc.setTextColor(0, 0, 0);
    doc.text(`Heli #${helicopter.heliIndex} -  Total Rentals: ${totalGuestRentals} Total Ski: ${String(totalski)} Total Snowboard: ${String(totalboard)}`, 10, yPos);
    yPos += 12;
  });

  //const today = new Date();
  //const dateString = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
  doc.save(`daily-rental-report-${formattedDate}.pdf`);
}
